import { Badge, Card, CardContent, cn } from "@safeplate/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  Info,
  RefreshCw,
  Smartphone,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import {
  getLocationDashboard,
  getOrganisationOverview,
  metricDefinition,
  type LocationDashboardMetrics,
} from "./dashboard-api.js";

function MetricCard({
  detail,
  href,
  icon: Icon,
  label,
  metric,
  tone = "neutral",
  value,
}: {
  detail: string;
  href: "/attention" | "/records" | "/setup" | "/tasks" | "/team";
  icon: LucideIcon;
  label: string;
  metric: string;
  tone?: "attention" | "danger" | "neutral" | "positive";
  value: number | string;
}) {
  return (
    <Link
      className="group block rounded-xl border border-stone-200 bg-surface p-4 transition hover:border-stone-300 sm:p-5"
      to={href}
    >
      <div className="flex items-start justify-between gap-3">
        <Icon
          className={cn(
            "size-4",
            tone === "danger"
              ? "text-red-700"
              : tone === "attention"
                ? "text-amber-700"
                : tone === "positive"
                  ? "text-emerald-800"
                  : "text-stone-500",
          )}
        />
        <span
          className="cursor-help text-stone-300"
          title={metricDefinition(metric)}
        >
          <Info className="size-3.5" />
        </span>
      </div>
      <p className="mt-4 text-2xl font-black tracking-tight text-stone-950">
        {value}
      </p>
      <p className="mt-1 text-xs font-black text-stone-700 group-hover:text-emerald-900">
        {label}
      </p>
      <p className="mt-1 text-[0.7rem] leading-4 text-stone-400">{detail}</p>
    </Link>
  );
}

function LocationMetrics({ metrics }: { metrics: LocationDashboardMetrics }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <MetricCard
        detail={`${metrics.dueToday.mandatory} plan · ${metrics.dueToday.business} business`}
        href="/tasks"
        icon={CalendarClock}
        label="Due today"
        metric="due"
        tone={metrics.dueToday.total ? "attention" : "positive"}
        value={metrics.dueToday.total}
      />
      <MetricCard
        detail={`${metrics.overdue.mandatory} plan · ${metrics.overdue.business} business`}
        href="/tasks"
        icon={AlertTriangle}
        label="Overdue"
        metric="overdue"
        tone={metrics.overdue.total ? "danger" : "positive"}
        value={metrics.overdue.total}
      />
      <MetricCard
        detail={`${metrics.outOfRange.fail} fail · ${metrics.outOfRange.warn} warning`}
        href="/records"
        icon={ClipboardCheck}
        label="Out of range"
        metric="range"
        tone={
          metrics.outOfRange.fail
            ? "danger"
            : metrics.outOfRange.warn
              ? "attention"
              : "positive"
        }
        value={metrics.outOfRange.total}
      />
      <MetricCard
        detail={`${metrics.attention.critical} critical · ${metrics.attention.major} major`}
        href="/attention"
        icon={AlertTriangle}
        label="Needs attention"
        metric="attention"
        tone={
          metrics.attention.critical
            ? "danger"
            : metrics.attention.total
              ? "attention"
              : "positive"
        }
        value={metrics.attention.total}
      />
      <MetricCard
        detail="Active devices with queued logs over 24 hours"
        href="/tasks"
        icon={Smartphone}
        label="Unsynced devices"
        metric="sync"
        value={metrics.unsyncedDevices ?? "—"}
      />
      <MetricCard
        detail={`${metrics.training.current} of ${metrics.training.total} active staff`}
        href="/team"
        icon={GraduationCap}
        label="Training current"
        metric="training"
        tone={
          metrics.training.current === metrics.training.total
            ? "positive"
            : "attention"
        }
        value={`${metrics.training.current}/${metrics.training.total}`}
      />
      <MetricCard
        detail="Still excluded with no return date"
        href="/records"
        icon={Stethoscope}
        label="Excluded staff"
        metric="excluded"
        tone={metrics.excludedStaff ? "danger" : "positive"}
        value={metrics.excludedStaff}
      />
      <MetricCard
        detail={`${metrics.comingUp} renewals / verifications within 60 days`}
        href="/setup"
        icon={RefreshCw}
        label="Coming up"
        metric="comingUp"
        tone={metrics.comingUp ? "attention" : "positive"}
        value={metrics.comingUp}
      />
    </div>
  );
}

export function DashboardPage() {
  const shell = useShell();
  const location = shell.activeLocation;
  const locationMetrics = useQuery({
    enabled: Boolean(location) && shell.activeRole !== "staff",
    queryFn: () => getLocationDashboard(location!.id),
    queryKey: ["dashboard", location?.id],
    refetchInterval: 60_000,
  });
  const overview = useQuery({
    enabled: shell.activeRole !== "staff" && shell.locations.length > 1,
    queryFn: getOrganisationOverview,
    queryKey: [
      "dashboard",
      "overview",
      shell.locations.map((entry) => entry.id),
    ],
    refetchInterval: 60_000,
  });

  if (shell.locationsPending)
    return <LoadingState label="Loading your locations" />;
  if (shell.activeRole === "staff")
    return (
      <EmptyState
        title="Your kitchen tasks"
        description="The organisation dashboard is for managers; your assigned checks are in Tasks."
        action={<Link to="/tasks">Open tasks</Link>}
      />
    );
  if (shell.locationError)
    return (
      <ErrorState
        message={displayError(shell.locationError)}
        onAction={() => void shell.refreshLocations()}
        title="Your locations aren’t available"
      />
    );
  if (!location)
    return (
      <>
        <header className="mb-7">
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Today
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Start with your first location
          </h1>
        </header>
        <EmptyState
          action={
            <Link
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-emerald-800 px-4 text-sm font-bold text-white"
              to="/setup"
            >
              Open setup <ArrowRight className="size-4" />
            </Link>
          }
          description="Add the restaurant, café or kitchen whose food-safety records you need to keep."
          title="No locations yet"
        />
      </>
    );

  return (
    <>
      <header className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Today ·{" "}
            {new Intl.DateTimeFormat("en-NZ", {
              dateStyle: "long",
              timeZone: location.timezone,
            }).format(new Date())}
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            {location.name}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Factual evidence counts for this location—never a claim of
            compliance or guaranteed verification.
          </p>
        </div>
        <Badge tone="positive">Kitchen can keep moving</Badge>
      </header>
      {locationMetrics.isPending ? (
        <LoadingState label="Calculating today’s evidence counts" />
      ) : null}
      {locationMetrics.error ? (
        <ErrorState
          message={displayError(locationMetrics.error)}
          onAction={() => void locationMetrics.refetch()}
          title="Today’s dashboard isn’t available"
        />
      ) : null}
      {locationMetrics.data ? (
        <>
          {locationMetrics.data.unavailable.length > 0 ? (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-900">
              Some counts are temporarily unavailable:{" "}
              {locationMetrics.data.unavailable.join(", ")}.
            </div>
          ) : null}
          <LocationMetrics metrics={locationMetrics.data} />
          <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_0.62fr]">
            <Card className="overflow-hidden border-emerald-900 bg-emerald-800 text-white">
              <CardContent className="relative p-6 sm:p-7">
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[0.65rem] font-bold tracking-widest text-emerald-300 uppercase">
                        Evidence completeness
                      </p>
                      <p className="mt-3 text-4xl font-black">
                        {locationMetrics.data.evidence.percentage === null
                          ? "—"
                          : `${locationMetrics.data.evidence.percentage}%`}
                      </p>
                    </div>
                    <span
                      className="cursor-help text-emerald-300"
                      title={metricDefinition("evidence")}
                    >
                      <Info className="size-4" />
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-emerald-100/70">
                    {locationMetrics.data.evidence.completed} of{" "}
                    {locationMetrics.data.evidence.expected} expected mandatory
                    record slots filled in the last 30 local dates.
                  </p>
                  <p className="mt-4 border-t border-white/10 pt-4 text-xs leading-5 text-emerald-100/55">
                    Coverage is factual evidence availability, not a legal
                    compliance score.
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <CheckCircle2 className="size-5 text-emerald-700" />
                <h2 className="mt-4 text-lg font-black">
                  Work from exceptions
                </h2>
                <p className="mt-2 text-sm leading-6 text-stone-500">
                  Staff keep completing checks even when a result is outside
                  target. Managers review what needs attention afterwards.
                </p>
                <Link
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-emerald-800"
                  to="/attention"
                >
                  Open attention queue <ArrowRight className="size-4" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}

      {shell.locations.length > 1 ? (
        <section className="mt-9">
          <div className="mb-4">
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Organisation overview
            </p>
            <h2 className="mt-1 text-xl font-black">Every location</h2>
          </div>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
            {overview.error ? (
              <ErrorState
                title="Overview isn’t available"
                message={displayError(overview.error)}
                onAction={() => void overview.refetch()}
              />
            ) : null}
            {overview.isPending ? (
              <LoadingState label="Loading organisation overview" />
            ) : null}
            {overview.data?.locations.map((entry) => {
              const metrics = entry.metrics;
              return (
                <Card key={entry.id}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-black">{entry.name}</p>
                        <p className="mt-1 text-xs capitalize text-stone-400">
                          {entry.status}
                        </p>
                      </div>
                      {overview.isFetching ? (
                        <RefreshCw className="size-3.5 animate-spin text-stone-300" />
                      ) : null}
                    </div>
                    {metrics ? (
                      <div className="mt-5 grid grid-cols-3 gap-2">
                        <div>
                          <p className="text-xl font-black">
                            {metrics.dueToday.total}
                          </p>
                          <p className="text-[0.65rem] text-stone-400">Due</p>
                        </div>
                        <div>
                          <p
                            className={cn(
                              "text-xl font-black",
                              metrics.attention.total && "text-amber-800",
                            )}
                          >
                            {metrics.attention.total}
                          </p>
                          <p className="text-[0.65rem] text-stone-400">
                            Attention
                          </p>
                        </div>
                        <div>
                          <p
                            className={cn(
                              "text-xl font-black",
                              metrics.outOfRange.total && "text-red-800",
                            )}
                          >
                            {metrics.outOfRange.total}
                          </p>
                          <p className="text-[0.65rem] text-stone-400">
                            Out of range
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-5 text-xs text-stone-400">
                        Loading factual counts…
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}
